import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Archive,
  ArrowLeft,
  Camera,
  MapPin,
  RotateCcw,
  Sparkles,
  Star,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import ApertureLoader from "@/components/ApertureLoader";
import { api } from "@/lib/api";

/**
 * Archive — the safety-net view for every soft-deleted applicant and
 * photographer. Admins land here from the top nav to restore records or
 * permanently purge them once they are sure.
 */
export default function ArchivePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ applicants: [], photographers: [] });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/archive");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load the archive.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const restore = async (kind, id) => {
    setBusyId(id);
    try {
      const path = kind === "applicant"
        ? `/admin/applicants/${id}/restore`
        : `/admin/photographers/${id}/restore`;
      await api.post(path);
      toast.success("Restored.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (kind, id, name) => {
    if (!window.confirm(`Permanently delete ${name}? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      const path = kind === "applicant"
        ? `/admin/archive/applicants/${id}`
        : `/admin/archive/photographers/${id}`;
      await api.delete(path);
      toast.success("Permanently deleted.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <ApertureLoader label="Loading archive…" />;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 md:py-10" data-testid="archive-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-mono-tag text-[10px] tracking-[0.25em] text-[#B08D57] mb-2">
            <Archive className="w-3.5 h-3.5" strokeWidth={2.25} />
            SAFETY NET · SOFT-DELETED RECORDS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Archive
          </h1>
          <p className="text-sm md:text-base text-[#4D3F35] mt-3 max-w-2xl">
            Everything you've deleted lives here. Restore in one click, or purge
            permanently once you're absolutely sure.
          </p>
        </div>
        <Button
          asChild
          variant="ghost"
          className="border border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-bold tracking-wide rounded-sm"
          data-testid="archive-back-btn"
        >
          <Link to="/admin">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
            Back to roster
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ArchiveSection
          title="Applicants"
          kicker="APPLICANT PIPELINE"
          icon={Sparkles}
          items={data.applicants}
          statusKey="stage"
          emptyText="No archived applicants."
          onRestore={(id) => restore("applicant", id)}
          onPurge={(id, name) => purge("applicant", id, name)}
          busyId={busyId}
          testIdPrefix="archived-applicant"
        />
        <ArchiveSection
          title="Photographers"
          kicker="ROSTER"
          icon={Camera}
          items={data.photographers}
          statusKey="status"
          emptyText="No archived photographers."
          onRestore={(id) => restore("photographer", id)}
          onPurge={(id, name) => purge("photographer", id, name)}
          busyId={busyId}
          testIdPrefix="archived-photographer"
        />
      </div>
    </div>
  );
}

function ArchiveSection({
  title, kicker, icon: Icon, items, statusKey, emptyText,
  onRestore, onPurge, busyId, testIdPrefix,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] p-5"
      data-testid={`archive-section-${testIdPrefix}`}
    >
      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-[#1A0F0A]/10">
        <Icon className="w-4 h-4 text-[#6B0F1A]" strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="font-mono-tag text-[9px] tracking-[0.25em] text-[#B08D57]">{kicker}</div>
          <div className="font-display text-lg font-extrabold tracking-tight text-[#1A0F0A]">
            {title}
            <span className="ml-2 font-mono-tag text-[10px] tracking-wider text-[#78584B]">
              {items.length}
            </span>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#78584B] leading-relaxed">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const isBusy = busyId === it.id;
            const location = it.resolved_city || it.region || "—";
            return (
              <li
                key={it.id}
                data-testid={`${testIdPrefix}-${it.id}`}
                className="group rounded-sm border border-[#1A0F0A]/8 bg-white/40 hover:bg-white/70 transition-colors p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-bold text-[#1A0F0A] truncate">
                      {it.name || it.email || "Unnamed"}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap font-mono-tag text-[9px] tracking-wider text-[#78584B]">
                      <span className="uppercase">{it[statusKey] || "—"}</span>
                      <span className="text-[#1A0F0A]/25">·</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2} />{location}</span>
                      {it.score > 0 && (
                        <>
                          <span className="text-[#1A0F0A]/25">·</span>
                          <span className="inline-flex items-center gap-0.5 text-[#B08D57]">
                            <Star className="w-3 h-3 fill-[#B08D57] text-[#B08D57]" strokeWidth={1.5} />
                            {it.score}
                          </span>
                        </>
                      )}
                    </div>
                    {it.email && (
                      <div className="mt-0.5 text-xs text-[#78584B] truncate">{it.email}</div>
                    )}
                    <div className="mt-1 font-mono-tag text-[9px] tracking-wide text-[#78584B]/70">
                      Archived {formatWhen(it.deleted_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      onClick={() => onRestore(it.id)}
                      disabled={isBusy}
                      variant="ghost"
                      size="sm"
                      className="border border-[#1B5E20]/30 hover:bg-[#1B5E20]/8 text-[#1B5E20] font-bold text-xs h-8 rounded-sm"
                      data-testid={`restore-${testIdPrefix}-${it.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                      Restore
                    </Button>
                    <Button
                      onClick={() => onPurge(it.id, it.name || it.email)}
                      disabled={isBusy}
                      variant="ghost"
                      size="sm"
                      className="border border-[#6B0F1A]/30 hover:bg-[#6B0F1A]/8 text-[#6B0F1A] font-bold text-xs h-8 rounded-sm"
                      data-testid={`purge-${testIdPrefix}-${it.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}

function formatWhen(iso) {
  if (!iso) return "recently";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "recently";
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
